import { displayImportedText } from '../services/displayLabels';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { measurementsApi, patientsApi, visitsApi } from '../services/api';
import { BiaAnalyzer } from './BiaAnalyzer';
import './clinic.css';
import VisitBilling from './VisitBilling';

const toInputDateTime = value => {
  const date = value ? new Date(value) : new Date();
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 16);
};
const numberOrNull = value => value === '' || value == null ? null : Number(value);
const formatDateTime = value => value ? new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function newVisit(patient, latestMeasurement) {
  return {
    startsAt: toInputDateTime(),
    completedAt: null,
    type: latestMeasurement ? 'Controllo' : 'Prima visita',
    status: 'In corso',
    reason: '',
    weightKg: latestMeasurement?.weightKg ?? '',
    waistCircumferenceCm: patient.waistCircumferenceCm ?? '',
    hipCircumferenceCm: patient.hipCircumferenceCm ?? '',
    clinicalNotes: '',
    nextSteps: ''
  };
}

export function VisitWorkspace() {
  const { id: patientId, visitId: routeVisitId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const copilotVisit = location.state?.copilotVisit;
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [draft, setDraft] = useState(null);
  const [currentVisitId, setCurrentVisitId] = useState(routeVisitId || '');
  const [showBia, setShowBia] = useState(searchParams.get('bia') === '1' && Boolean(routeVisitId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const [patientData, visitItems, measurementItems] = await Promise.all([
        patientsApi.get(patientId),
        visitsApi.getAll(patientId, 50),
        measurementsApi.getByPatient(patientId, 50)
      ]);
      setPatient(patientData);
      setVisits(visitItems || []);
      setMeasurements(measurementItems || []);
      if (routeVisitId) {
        const existing = await visitsApi.get(routeVisitId);
        if (existing.patientId !== patientId) throw new Error('La visita non appartiene al paziente selezionato.');
        setDraft({ ...existing, startsAt: toInputDateTime(existing.startsAt) });
        setCurrentVisitId(existing.id);
      } else {
        const visitDraft = newVisit(patientData, measurementItems?.[0]);
        setDraft(copilotVisit ? { ...visitDraft, ...copilotVisit } : visitDraft);
      }
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [patientId, routeVisitId, copilotVisit]);

  useEffect(() => { load(); }, [load]);

  const latestMeasurement = measurements[0];
  const previousVisit = visits.find(visit => visit.id !== currentVisitId);
  const change = event => setDraft(value => ({ ...value, [event.target.name]: event.target.value }));

  const payload = useCallback((status = draft?.status, completedAt = draft?.completedAt) => ({
    patientId,
    startsAt: new Date(draft.startsAt).toISOString(),
    completedAt,
    type: draft.type,
    status,
    reason: draft.reason || null,
    weightKg: numberOrNull(draft.weightKg),
    waistCircumferenceCm: numberOrNull(draft.waistCircumferenceCm),
    hipCircumferenceCm: numberOrNull(draft.hipCircumferenceCm),
    clinicalNotes: draft.clinicalNotes || null,
    nextSteps: draft.nextSteps || null
  }), [draft, patientId]);

  const persistVisit = async (status = draft.status, completedAt = draft.completedAt) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const saved = currentVisitId
        ? await visitsApi.update(currentVisitId, payload(status, completedAt))
        : await visitsApi.create(payload(status, completedAt));
      setCurrentVisitId(saved.id);
      setDraft({ ...saved, startsAt: toInputDateTime(saved.startsAt) });
      if (!currentVisitId) navigate(`/patients/${patientId}/visits/${saved.id}`, { replace: true });
      setMessage(status === 'Completata' ? 'Visita conclusa e salvata.' : 'Visita salvata.');
      return saved;
    } catch (saveError) {
      setError(saveError.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const startBia = async () => {
    if (!draft.weightKg || Number(draft.weightKg) <= 0) {
      setError('Inserisci il peso rilevato prima di avviare la BIA.');
      return;
    }
    const visit = await persistVisit('In corso', null);
    if (visit) setShowBia(true);
  };

  const completeVisit = async () => {
    await persistVisit('Completata', new Date().toISOString());
  };

  const onMeasurementSaved = saved => {
    setMeasurements(items => [saved, ...items]);
    setDraft(value => ({ ...value, weightKg: saved.weightKg, measurementCount: (value.measurementCount || 0) + 1 }));
    setMessage('BIA acquisita e associata alla visita.');
  };

  const visitTitle = useMemo(() => {
    if (!draft) return 'Visita';
    return currentVisitId ? `${draft.type} del ${formatDateTime(draft.startsAt)}` : 'Nuova visita';
  }, [draft, currentVisitId]);

  if (!patient || !draft) return <div className="clinic-page"><div className="clinic-empty">Preparazione della visita…</div></div>;

  return <div className="clinic-page visit-workspace">
    <header className="clinic-head visit-head">
      <div className="workspace-summary">
        <span className="clinic-avatar">{patient.firstName?.[0]}{patient.lastName?.[0]}</span>
        <div>
          <h1>{visitTitle}</h1>
          <p><strong>{patient.lastName} {patient.firstName}</strong> · {displayImportedText(patient.code) || 'Senza codice'} · {draft.status}</p>
        </div>
      </div>
      <Link className="clinic-button secondary" to={`/patients/${patientId}`}>Torna alla cartella</Link>
    </header>

    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {message && <div className="alert alert-success" role="status">{message}</div>}

    <section className="visit-context" aria-label="Contesto clinico">
      <div><span>Ultima BIA</span><strong>{latestMeasurement ? formatDateTime(latestMeasurement.measuredAt) : 'Nessuna'}</strong></div>
      <div><span>Ultimo peso</span><strong>{latestMeasurement?.weightKg ? `${latestMeasurement.weightKg.toFixed(1)} kg` : '—'}</strong></div>
      <div><span>Ultimo BMI</span><strong>{latestMeasurement?.bodyComposition?.bmi?.toFixed(1) || '—'}</strong></div>
      <div><span>Visita precedente</span><strong>{previousVisit ? formatDateTime(previousVisit.startsAt) : 'Nessuna'}</strong></div>
    </section>

    <div className="visit-layout">
      <main className="visit-main">
        <section className="clinic-panel clinic-form visit-form-section">
          <div className="clinic-panel-head"><div><h2>Dati della visita</h2><p>Rilevazioni e motivo del controllo corrente.</p></div></div>
          <div className="clinic-grid three">
            <label>Data e ora<input name="startsAt" type="datetime-local" value={draft.startsAt} onChange={change}/></label>
            <label>Tipo di visita<select name="type" value={draft.type} onChange={change}><option>Prima visita</option><option>Controllo</option><option>Follow-up remoto</option></select></label>
            <label>Motivo della visita<input name="reason" value={draft.reason || ''} onChange={change} placeholder="Es. controllo composizione corporea"/></label>
            <label>Peso rilevato (kg)<input name="weightKg" type="number" min="5" max="300" step="0.1" value={draft.weightKg ?? ''} onChange={change}/></label>
            <label>Circonferenza vita (cm)<input name="waistCircumferenceCm" type="number" min="20" max="250" step="0.1" value={draft.waistCircumferenceCm ?? ''} onChange={change}/></label>
            <label>Circonferenza fianchi (cm)<input name="hipCircumferenceCm" type="number" min="20" max="250" step="0.1" value={draft.hipCircumferenceCm ?? ''} onChange={change}/></label>
          </div>
        </section>

        <section className="clinic-panel clinic-form visit-form-section">
          <div className="clinic-panel-head"><div><h2>Valutazione e piano</h2><p>Annotazioni riferite esclusivamente a questo incontro.</p></div></div>
          <div className="clinic-grid two">
            <label>Valutazione clinica<textarea name="clinicalNotes" value={draft.clinicalNotes || ''} onChange={change} placeholder="Osservazioni, aderenza, sintomi ed elementi emersi durante la visita…"/></label>
            <label>Indicazioni e prossimi passi<textarea name="nextSteps" value={draft.nextSteps || ''} onChange={change} placeholder="Obiettivi concordati, controlli richiesti e data del prossimo incontro…"/></label>
          </div>
        </section>
      </main>

      <aside className="visit-actions" aria-label="Azioni della visita">
        <div className="visit-actions-head"><h2>Azioni della visita</h2><span className={`visit-status ${draft.status === 'Completata' ? 'complete' : ''}`}>{draft.status}</span></div>
        <div className="visit-action-block">
          <strong>Bioimpedenziometria</strong>
          <p>{draft.measurementCount ? `${draft.measurementCount} BIA associata alla visita.` : 'Nessuna BIA associata alla visita.'}</p>
          <button className="clinic-button visit-primary-action" onClick={startBia} disabled={saving}>{showBia ? 'Torna all’acquisizione BIA' : 'Avvia nuova BIA'}</button>
        </div>
        <button className="clinic-button secondary" onClick={() => persistVisit()} disabled={saving}>{saving ? 'Salvataggio…' : 'Salva visita'}</button>
        <button className="clinic-button visit-complete" onClick={completeVisit} disabled={saving || draft.status === 'Completata'}>Concludi visita</button>
        <p className="visit-save-note">La BIA viene salvata nella cartella del paziente e collegata a questa visita.</p>
      </aside>
    </div>

    {currentVisitId && <VisitBilling key={currentVisitId} visitId={currentVisitId} patient={patient} visitStatus={draft.status} />}

    {showBia && currentVisitId && <section className="visit-bia-section" id="acquisizione-bia">
      <div className="visit-bia-heading"><div><h2>Acquisizione BIA</h2><p>Il paziente e la visita sono già selezionati. Collega BioSmart e avvia la lettura.</p></div><button className="clinic-button secondary" onClick={() => setShowBia(false)}>Chiudi acquisizione</button></div>
      <BiaAnalyzer patientId={patientId} visitId={currentVisitId} initialWeightKg={draft.weightKg} embedded onMeasurementSaved={onMeasurementSaved}/>
    </section>}
  </div>;
}

export default VisitWorkspace;
